import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Pagination from '../../Pagination';
import { api } from '../../../utils/api';
import { fmtDateTime, money, todayISO } from '../../../utils/region';
import { statutoryBookName } from '../../../utils/countryFeatures';
import {
  payrollRunsApi, PRR_MAX_TEXT, PRR_PAY_BOOKS, PRR_PAY_MAX_LINES, PRR_PAY_METHODS,
  PrrBankLineBrief, PrrCashAccount, PrrPage, PrrPayBook, PrrPayInput, PrrPayLine, PrrPayMethod, PrrPayOptions, PrrPayPreview, PrrPayRefusal,
  PrrPayReversePreview, PrrPayment, PrrPaymentStatus, PrrRunBody,
} from '../../../utils/api/payrollRuns';
import type { AwCtx } from '../ctx';
import { card, dmy, errBox, inp, note, num, pillAmber, pillGreen, pillGrey, pillRed, sectionHead, table, td, th } from '../ui';
import { Dialog, Foot, Loader, actBtn, emptyLine, field, lbl } from '../dialog';
import { EntryLines } from './PayrollRuns';
import { TF, fieldLabel } from './payrollText';

// ── Payroll › Payroll List: paying a posted run (batch 10 R8 — PAY-11's payment axis, PAY-15, PAY-12 gate 3, PAY-5's
// $ tier; design §3.3, §3.4.3, §4 R8; the R8 brief §3 package C tasks 3 – 5) ──────────────────────────────────────
// Three things live here, beside the run window (PayrollRuns.tsx, R8-21: that file is long enough already):
//   · Record payment (PayDialog, full screen — the lines, their Pay now, the fields and the server's dry run of the entry);
//   · the run window's Payments tab (PaymentsTab) — each payment, its items, its entry, its reversal;
//   · Reverse a payment (ReversePaymentDialog, a card — its form is the reason).
// ⛔ The SERVER plans every payment (services/payrollPay.ts, package A). GET …/payment-options says which lines can be
// paid and how much is unpaid on each, with every employee's pay details read off the LIVE master (R8-3); GET
// …/payment-preview reads the plan the POST runs again inside its transaction (always 200, R8-1) — the refusals, the
// entry to the satang, the statement lines of exactly the total, what each line is left with. Record payment is pressed
// only on a preview of exactly what it sends that says ok, so a POST the server is certain to refuse is never sent (a
// refused non-GET pages the admins); a 409 that says the figures moved reads both again and says "check again".
// ⛔ Satang in, and the unpaid figure the screen SAW goes with every line (R8-2): a second tab, or a double click, cannot
// pay a line twice in part — the server answers stale_unpaid. Pay now is typed DOWN from the unpaid amount, never above.
// ⛔ One payment holds at most PRR_PAY_MAX_LINES lines (one page; R8 review L3): the preview's GET carries every line
// picked, and a longer query is refused before it reaches the server (414 / 431) — so the dialog says so where the ticks
// are once the run has more lines than that, and cannot tick a 101st (tick-all ticks only up to it). The server refuses
// more with too_many_lines; the screen never sends it.
// ⛔ A line whose employee lacks the pay details ($ tier) cannot be ticked: the row says what is missing, in Employees'
// own words, and its Fix opens the employee through the shell (ctx.setNav — the shell asks first while this is dirty).
// ⛔ A read-only seat never gets here (the run window draws Record payment and Reverse only for a seat that may write).
// ⛔ The preview is asked only for a complete, well-formed payment — a line ticked with a figure, a date, an account, and
// the bank book's volume when it is the bank book: a GET with a malformed query would be refused (400) and page the
// admins just as a POST would.
// ⛔ Statutory names (ภ.ง.ด.1, สปส.) never appear inside a translation; amounts print through money().

const m2 = (sat: any): string => money((Number(sat) || 0) / 100);
const isIso = (s: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(String(s || ''));
/** A typed amount → satang: digits, at most two decimals, commas allowed. null = not a figure above zero. */
function toSat(text: string): number | null {
  const s = String(text ?? '').replace(/,/g, '').trim();
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  const n = Math.round(Number(s) * 100);
  return n > 0 ? n : null;
}
const satText = (sat: number): string => (sat > 0 ? (sat / 100).toFixed(2) : '');
const linkBtn: React.CSSProperties = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent)', fontWeight: 700, fontSize: 12 };
const amberBox: React.CSSProperties = { border: '1px solid #F1C232', background: '#FFFBEB' };
const redBox: React.CSSProperties = { border: '1px solid #F3B4AE', background: '#FEF3F2' };
const kv = (label: string, value: React.ReactNode, attr?: string) => (
  <div style={{ minWidth: 130 }} data-kv={attr}>
    <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{label}</div>
    <div style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
  </div>
);
/** A payment status as a chip: paid green, partially paid amber, unpaid grey. */
export const payPill = (s: string): React.CSSProperties => (s === 'paid' ? pillGreen : s === 'partial' ? pillAmber : pillGrey);
export function PayStatusChip({ status, t }: { status: PrrPaymentStatus | string; t: TF }) {
  return <span style={payPill(status)} data-pay-status={status}>{t(`prr_pay_status_${status}`)}</span>;
}
/** A book by its statutory name where the country pack gives one, else the screen's own word for the two a payment uses. */
export const bookWord = (t: TF, book: string): string =>
  statutoryBookName(book) || ((PRR_PAY_BOOKS as readonly string[]).includes(book) ? t(`prr_pay_book_${book}`) : book);
/** A statement line as a person recognises it: date, what the bank wrote, the amount, the account it is on. */
export const lineText = (l: { txnDate: string; description: string; debitSat: number; accountNumber?: string }): string =>
  [dmy(l.txnDate), l.description, m2(l.debitSat), l.accountNumber || ''].filter(Boolean).join(' · ');
/** A cash account as the pickers show it, in the reader's language. */
export function useAccountName() {
  const { i18n } = useTranslation();
  const thai = i18n.language === 'th';
  return (a: { code: string; nameTh: string; nameEn: string } | null | undefined): string => (a ? `${a.code} ${thai ? a.nameTh || a.nameEn : a.nameEn || a.nameTh}` : '');
}
/** One refusal of a payment or of its reversal (services/payrollPay.ts PAY_REFUSALS ∪ PAY_REVERSE_REFUSALS) in the
 *  screen's words: the figures it names in money(), the fields in Employees' words, the account by its code. */
export function payRefusalText(t: TF, r: PrrPayRefusal, accounts: PrrCashAccount[] = []): string {
  const k = `prr_pref_${r.code}`;
  const code = (id?: string) => accounts.find(a => a.id === id)?.code || '';
  const opts = {
    unpaid: m2(r.unpaidSat), amount: m2(r.amountSat), line: m2(r.lineSat), total: m2(r.totalSat), date: r.date ? dmy(r.date) : '',
    fields: (r.fields || []).map(f => fieldLabel(t, f)).join(', '), account: code(r.pairedAccountId) || code(r.accountId), message: String(r.message || ''),
    max: String(r.max ?? ''), n: String(r.count ?? ''),
  };
  return t(k) !== k ? t(k, opts) : t('prr_pref_other');
}
/** The payment routes' refusal of a write (PAY_ERRORS — `refused` carries refusals, `ledger` the door's own sentence). */
export function payErr(e: any, t: TF): string {
  if (e?.readOnlySeat) return String(e.message || '');
  const p = e?.payload || {};
  const code = String(p.code || '');
  if (code === 'pay_forbidden') return t('prr_err_pay_forbidden');
  const k = `prr_pay_err_${code}`;
  if (code && t(k) !== k) return t(k, { field: String(p.field || ''), message: String(p.message || p.error || '') });
  return String(e?.message || '') || t('prr_err_generic');
}
const whoOf = (r: PrrPayRefusal): string => String(r.employeeName || '');
/** The red list of a dry run's refusals, one per line, with the employee's Fix where the employee is what is missing. */
export function PayRefusals({ refusals, t, accounts, fix }: { refusals: PrrPayRefusal[]; t: TF; accounts?: PrrCashAccount[]; fix?: (r: PrrPayRefusal) => React.ReactNode }) {
  return (
    <div style={{ ...card, ...redBox, display: 'flex', flexDirection: 'column', gap: 5 }} data-pay-refusals={refusals.length}>
      {refusals.map((r, i) => (
        <div key={`${r.code}:${r.lineId || r.id || ''}:${i}`} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12.5, color: '#B42318' }} data-refusal={r.code} data-line={r.lineId || undefined}>
          <i className="ti ti-circle-x" aria-hidden />
          <div style={{ flex: 1 }}>{whoOf(r) && <b>{whoOf(r)} — </b>}<span data-refusal-text>{payRefusalText(t, r, accounts)}</span></div>
          {fix ? fix(r) : null}
        </div>
      ))}
    </div>
  );
}
/** The pay date a payment starts from: the run's own when it is set and not in a month still ahead, else today (the
 *  tenant's day) — R8-15 refuses a month ahead, so it is never offered. */
const startDate = (runPayDate: string): string => (isIso(runPayDate) && runPayDate.slice(0, 7) <= todayISO().slice(0, 7) ? runPayDate : todayISO());

// ── Record payment (full screen; its form the ticks, the amounts and the fields) ───────────────────────────────
type Picked = { unpaidSat: number; name: string };
export function PayDialog({ ctx, body, onClose, onDone }: { ctx: AwCtx; body: PrrRunBody; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const acctName = useAccountName();
  const run = body.run;
  const [page, setPage] = useState(1);
  const [opts, setOpts] = useState<PrrPayOptions | null>(null);
  const [reload, setReload] = useState(0);
  // Ticks and typed amounts are kept by line, across the pages of lines (100 a page).
  const [picks, setPicks] = useState<Record<string, Picked>>({});
  const [texts, setTexts] = useState<Record<string, string>>({});
  const [payDate, setPayDate] = useState(() => startDate(run.payDate));
  const [method, setMethod] = useState<PrrPayMethod>('transfer');
  const [book, setBook] = useState<PrrPayBook>('bank');
  const [accountId, setAccountId] = useState('');
  const [volume, setVolume] = useState('');
  const [volumes, setVolumes] = useState<string[]>([]);
  const [line, setLine] = useState<PrrBankLineBrief | null>(null);
  const [slip, setSlip] = useState('');
  const [noteText, setNoteText] = useState('');
  const [pv, setPv] = useState<{ key: string; p: PrrPayPreview } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [changed, setChanged] = useState(false);
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };
  useEffect(() => {
    let alive = true;
    payrollRunsApi.paymentOptions(ctx.clientId, run.id, page).then(o => {
      if (!alive) return;
      setOpts(o); setErr('');
      // A line still ticked takes the unpaid figure this answer gives — that is now the one the screen saw (R8-2) — and
      // one this answer says can no longer be paid (paid meanwhile, its pay details gone) is unticked.
      setPicks(x => {
        const n = { ...x };
        for (const l of o.lines.rows) if (n[l.lineId]) { if (l.payReady && l.unpaidSat > 0) n[l.lineId] = { ...n[l.lineId], unpaidSat: l.unpaidSat }; else delete n[l.lineId]; }
        return n;
      });
    }).catch((e: any) => { if (alive) setErr(payErr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, run.id, page, reload]);   // eslint-disable-line react-hooks/exhaustive-deps
  // The bank book's volumes this client's books already use (the house list, used most first): the first is offered,
  // a new one is typed. Not an edit — the person has typed nothing.
  useEffect(() => {
    let alive = true;
    api.jnlBankAccounts(ctx.clientId).then((d: any) => {
      if (!alive) return;
      const list = (d?.accounts || []).map((a: any) => String(a.id)).filter(Boolean);
      setVolumes(list);
      setVolume(v => v || list[0] || '');
    }).catch(() => { if (alive) setVolumes([]); });
    return () => { alive = false; };
  }, [ctx.clientId]);
  const accounts = opts?.accounts || [];
  const items = useMemo(() => Object.entries(picks).map(([lineId, p]) => ({ lineId, amountSat: toSat(texts[lineId] ?? satText(p.unpaidSat)), unpaidSat: p.unpaidSat })), [picks, texts]);
  const itemsOk = items.length > 0 && items.every(i => i.amountSat !== null && i.amountSat <= i.unpaidSat);
  const totalSat = items.reduce((s, i) => s + (i.amountSat || 0), 0);
  // What the preview and the POST carry — only when it is complete and well-formed (see the head of the file).
  const input: PrrPayInput | null = !itemsOk || !isIso(payDate) || !accountId || (book === 'bank' && !volume.trim()) ? null : {
    items: items.map(i => ({ lineId: i.lineId, amountSat: i.amountSat as number, unpaidSat: i.unpaidSat })), payDate, method, sourceBook: book, accountId,
    ...(book === 'bank' ? { bankBookAccount: volume.trim() } : {}), ...(line ? { bankLineId: line.id } : {}), ...(slip.trim() ? { slipRef: slip.trim() } : {}),
    ...(noteText.trim() ? { note: noteText.trim() } : {}),
  };
  const key = input ? JSON.stringify(input) : '';
  // The dry run, 300 ms after the last change (R8-1: the plan the POST runs again).
  useEffect(() => {
    if (!key) return;
    let alive = true;
    const x = JSON.parse(key) as PrrPayInput;
    const h = setTimeout(() => {
      payrollRunsApi.paymentPreview(ctx.clientId, run.id, x).then(p => { if (alive) { setPv({ key, p }); setErr(''); } }).catch((e: any) => { if (alive) setErr(payErr(e, t)); });
    }, 300);
    return () => { alive = false; clearTimeout(h); };
  }, [ctx.clientId, run.id, key, reload]);   // eslint-disable-line react-hooks/exhaustive-deps
  const p = pv && pv.key === key ? pv.p : null;
  const ready = !!input && !!p && p.ok && !busy;
  /** R8-4: a transfer goes in the bank book, cash in the cash book, "other" in either; a statement line needs the bank book. */
  const chooseMethod = (m: PrrPayMethod) => {
    setMethod(m);
    if (m === 'transfer') setBook('bank');
    if (m === 'cash') { setBook('cash'); setLine(null); }
  };
  /** A statement line picked fills (and holds) the date, and the account when its bank account is paired (R8-5). */
  const pickLine = (b: PrrBankLineBrief | null) => {
    setDirty(true); setLine(b);
    if (!b) return;
    setPayDate(b.txnDate); setBook('bank');
    if (b.pairedAccountId) setAccountId(b.pairedAccountId);
  };
  /** Tick (or untick) lines — never past PRR_PAY_MAX_LINES: those already ticked keep their place, the rest are ticked in
   *  the page's order while there is room. */
  const tickLines = (ls: PrrPayLine[], on: boolean) => {
    const add = on ? ls.filter(l => !picks[l.lineId]).slice(0, Math.max(0, PRR_PAY_MAX_LINES - Object.keys(picks).length)) : [];
    setPicks(x => {
      const n = { ...x };
      if (!on) for (const l of ls) delete n[l.lineId];
      for (const l of add) if (!n[l.lineId] && Object.keys(n).length < PRR_PAY_MAX_LINES) n[l.lineId] = { unpaidSat: l.unpaidSat, name: l.name };
      return n;
    });
    const fill = add.filter(l => texts[l.lineId] === undefined);
    if (fill.length) setTexts(x => ({ ...x, ...Object.fromEntries(fill.map(l => [l.lineId, satText(l.unpaidSat)])) }));
  };
  const payable = (l: PrrPayLine) => l.payReady && l.unpaidSat > 0;
  const rows = opts?.lines.rows || [];
  const allOn = rows.filter(payable).length > 0 && rows.filter(payable).every(l => !!picks[l.lineId]);
  const full = items.length >= PRR_PAY_MAX_LINES;
  const submit = async () => {
    if (!ready || !input) return;
    setBusy(true); setErr(''); setChanged(false);
    try {
      await payrollRunsApi.recordPayment(ctx.clientId, run.id, input);
      setDirty(false); onDone();
    } catch (e: any) {
      // The figures moved under the preview (another payment, a master edit): read both again and say so.
      if (e?.payload?.code === 'refused') setChanged(true); else setErr(payErr(e, t));
      setPv(null); setReload(n => n + 1);
    } finally { setBusy(false); }
  };
  const fixEmp = (id: string) => (
    <button style={linkBtn} onClick={() => ctx.setNav('payroll', 'employees', { emp: id, run: '' })} data-pay-fix={id}>{t('prr_fix')} <i className="ti ti-arrow-right" aria-hidden /></button>
  );
  const refusalFix = (r: PrrPayRefusal) => (r.code === 'tier_pay' && r.employeeId ? fixEmp(String(r.employeeId)) : null);
  const locked = !!line;
  const e = p?.entry || null;
  return (
    <Dialog fullscreen title={t('prr_pay_title')} docNo={p?.payNo ? `${run.docNo} · ${p.payNo}` : run.docNo} onClose={onClose} busy={busy} dirty={dirty}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon="ti ti-cash" label={t('prr_pay_go')} t={t} />}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-pay-form>
        <div style={note}>{t('prr_pay_note')}</div>
        {changed && <div style={{ ...card, ...amberBox, fontSize: 12.5, color: '#92400E' }} data-pay-changed>{t('prr_pay_changed')}</div>}
        {err && <div style={errBox} data-pay-error>{err}</div>}
        {!opts ? (err ? null : <Loader />) : (
          <>
            {opts.refusals.length > 0 && <PayRefusals refusals={opts.refusals} t={t} accounts={accounts} />}
            {opts.lines.total > PRR_PAY_MAX_LINES && (
              <div style={{ ...(full ? { ...card, ...amberBox, color: '#92400E' } : note), fontSize: 12.5 }} data-pay-cap={items.length} data-pay-cap-full={full ? '1' : '0'}>
                <i className="ti ti-info-circle" aria-hidden /> {t('prr_pay_cap', { max: PRR_PAY_MAX_LINES, n: items.length })}
              </div>
            )}
            <section style={{ ...card, padding: 0, overflowX: 'auto' }} data-pay-lines={opts.lines.total}>
              <table style={table}>
                <thead>
                  <tr>
                    <th style={{ ...th, width: 30 }}>
                      <input type="checkbox" checked={allOn} disabled={!rows.some(payable) || (full && !allOn)} aria-label={t('prr_pay_tick_all')} title={t('prr_pay_tick_all')} data-pay-tick-all
                        onChange={ev => { setDirty(true); tickLines(rows.filter(payable), ev.target.checked); }} />
                    </th>
                    <th style={th}>{t('prr_col_employee')}</th><th style={th}>{t('prr_col_paid_to')}</th>
                    <th style={{ ...th, textAlign: 'right' }}>{t('prr_col_net')}</th><th style={{ ...th, textAlign: 'right' }}>{t('prr_col_paid')}</th>
                    <th style={{ ...th, textAlign: 'right' }}>{t('prr_col_unpaid')}</th><th style={{ ...th, textAlign: 'right' }}>{t('prr_col_pay_now')}</th>
                    <th style={th}>{t('prr_col_payment')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(l => {
                    const on = !!picks[l.lineId];
                    const typed = texts[l.lineId] ?? satText(l.unpaidSat);
                    const sat = toSat(typed);
                    const bad = on && (sat === null || sat > (picks[l.lineId]?.unpaidSat ?? l.unpaidSat));
                    const missing = (l.payIssues || []).map(i => fieldLabel(t, i.field)).join(', ');
                    return (
                      <tr key={l.lineId} data-pay-line={l.lineId} data-ready={l.payReady ? '1' : '0'} data-on={on ? '1' : '0'}
                        title={!l.payReady ? t('prr_pay_not_ready', { fields: missing || t('prr_none_dash') }) : undefined}
                        style={!payable(l) ? { color: 'var(--text-tertiary)' } : undefined}>
                        <td style={td}>
                          {payable(l) ? <input type="checkbox" checked={on} disabled={full && !on} title={full && !on ? t('prr_pay_cap_full', { max: PRR_PAY_MAX_LINES }) : undefined}
                            onChange={ev => { setDirty(true); tickLines([l], ev.target.checked); }} aria-label={l.name} data-pay-tick={l.lineId} />
                            : !l.payReady ? <i className="ti ti-lock" aria-hidden data-pay-locked={l.lineId} /> : null}
                        </td>
                        <td style={{ ...td, minWidth: 180 }}>
                          <b>{l.name}</b><div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{l.empCode}</div>
                          {!l.payReady && (
                            <div style={{ fontSize: 11.5, color: '#92400E', display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }} data-pay-missing={l.lineId}>
                              <span>{t('prr_pay_not_ready', { fields: missing || t('prr_none_dash') })}</span>{fixEmp(l.employeeId)}
                            </div>
                          )}
                        </td>
                        <td style={{ ...td, whiteSpace: 'nowrap', fontSize: 12 }}>
                          {l.payMethod ? t(`prl_opt_pay_method_${l.payMethod}`) : '—'}
                          {l.bankName || l.bankLast4 ? <div style={{ color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{[l.bankName, l.bankLast4 ? `••••${l.bankLast4}` : ''].filter(Boolean).join(' ')}</div> : null}
                        </td>
                        <td style={num} data-net={l.netSat}>{m2(l.netSat)}</td>
                        <td style={num} data-paid={l.paidSat}>{m2(l.paidSat)}</td>
                        <td style={num} data-unpaid={l.unpaidSat}>{m2(l.unpaidSat)}</td>
                        <td style={num}>
                          {on ? (
                            <>
                              <input value={typed} onChange={ev => { setDirty(true); setTexts(x => ({ ...x, [l.lineId]: ev.target.value })); }} inputMode="decimal"
                                style={{ ...inp, width: 110, textAlign: 'right', ...(bad ? amberBox : {}) }} aria-label={`${l.name} · ${t('prr_col_pay_now')}`} data-pay-now={l.lineId} />
                              {bad && <div style={{ fontSize: 11, color: '#B42318', whiteSpace: 'normal' }} data-pay-now-bad={l.lineId}>{t('prr_pay_now_bad', { amount: m2(picks[l.lineId]?.unpaidSat ?? l.unpaidSat) })}</div>}
                            </>
                          ) : <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                        </td>
                        <td style={td}><PayStatusChip status={l.status} t={t} /></td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td style={{ ...td, fontWeight: 800 }} colSpan={6}>{t('prr_pay_total', { count: items.length })}</td>
                    <td style={{ ...num, fontWeight: 800 }} data-pay-total={totalSat}>{m2(totalSat)}</td><td style={td} />
                  </tr>
                </tfoot>
              </table>
            </section>
            <Pagination page={page} total={opts.lines.total} limit={opts.lines.limit || 100} onPage={setPage} />
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 1fr) minmax(420px, 1.4fr)', gap: 12, alignItems: 'start' }}>
              <section style={{ ...card, display: 'flex', flexDirection: 'column', gap: 10 }} data-pay-fields>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label htmlFor="prr-pay-date" style={lbl}>{t('prr_pay_f_date')}</label>
                    <input id="prr-pay-date" type="date" value={payDate} disabled={locked} onChange={ev => ed(setPayDate, ev.target.value)} style={field} data-pay-f="date" />
                  </div>
                  <div>
                    <label htmlFor="prr-pay-method" style={lbl}>{t('prr_pay_f_method')}</label>
                    <select id="prr-pay-method" value={method} onChange={ev => { setDirty(true); chooseMethod(ev.target.value as PrrPayMethod); }} style={field} data-pay-f="method">
                      {PRR_PAY_METHODS.map(m => <option key={m} value={m}>{t(`prr_pay_method_${m}`)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="prr-pay-book" style={lbl}>{t('prr_pay_f_book')}</label>
                    <select id="prr-pay-book" value={book} disabled={method !== 'other' || locked} onChange={ev => ed(setBook, ev.target.value as PrrPayBook)} style={field} data-pay-f="book">
                      {PRR_PAY_BOOKS.map(b => <option key={b} value={b}>{t(`prr_pay_book_${b}`)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="prr-pay-account" style={lbl}>{t('prr_pay_f_account')}</label>
                    <select id="prr-pay-account" value={accountId} disabled={locked && !!line?.pairedAccountId} onChange={ev => ed(setAccountId, ev.target.value)} style={field} data-pay-f="account">
                      <option value="">{t('prr_choose')}</option>
                      {accounts.map(a => <option key={a.id} value={a.id}>{acctName(a)}</option>)}
                    </select>
                  </div>
                </div>
                {book === 'bank' && (
                  <div>
                    <label htmlFor="prr-pay-volume" style={lbl}>{t('prr_pay_f_volume')}</label>
                    <input id="prr-pay-volume" list="prr-pay-volumes" value={volume} onChange={ev => ed(setVolume, ev.target.value)} maxLength={PRR_MAX_TEXT} placeholder={t('prr_pay_volume_ph')} style={field} data-pay-f="volume" />
                    <datalist id="prr-pay-volumes">{volumes.map(v => <option key={v} value={v} />)}</datalist>
                  </div>
                )}
                <div data-pay-bank-line>
                  <div style={lbl}>{t('prr_pay_f_line')}</div>
                  {line ? (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 12.5 }} data-pay-line-picked={line.id}>
                      <i className="ti ti-lock" aria-hidden /> <span>{lineText(line)}</span>
                      <button style={linkBtn} onClick={() => pickLine(null)} data-pay-line-drop>{t('prr_pay_line_drop')}</button>
                      <div style={{ ...note, width: '100%' }}>{t(line.pairedAccountId ? 'prr_pay_line_locked_account' : 'prr_pay_line_locked')}</div>
                    </div>
                  ) : !opts.hasMerchant ? <div style={note} data-pay-no-merchant>{t('prr_pay_line_no_merchant')}</div>
                    : book !== 'bank' ? <div style={note}>{t('prr_pay_line_bank_only')}</div>
                    : !p ? <div style={note}>{t('prr_pay_line_wait')}</div>
                    : !(p.bankLines?.rows || []).length ? <div style={note} data-pay-no-lines>{t('prr_pay_line_none', { amount: m2(p.totalSat) })}</div> : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }} data-pay-line-offers={(p.bankLines?.rows || []).length}>
                        {(p.bankLines?.rows || []).map(b => (
                          <label key={b.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12.5 }}>
                            <input type="radio" name="prr-pay-line" checked={false} onChange={() => pickLine(b)} data-pay-line-offer={b.id} />
                            <span>{lineText(b)}</span>
                          </label>
                        ))}
                      </div>
                    )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label htmlFor="prr-pay-slip" style={lbl}>{t('prr_pay_f_slip')}</label>
                    <input id="prr-pay-slip" value={slip} onChange={ev => ed(setSlip, ev.target.value)} maxLength={PRR_MAX_TEXT} style={field} data-pay-f="slip" />
                  </div>
                  <div>
                    <label htmlFor="prr-pay-note" style={lbl}>{t('prr_pay_f_note')}</label>
                    <input id="prr-pay-note" value={noteText} onChange={ev => ed(setNoteText, ev.target.value)} maxLength={PRR_MAX_TEXT} style={field} data-pay-f="note" />
                  </div>
                </div>
              </section>
              <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }} data-pay-preview={p ? (p.ok ? 'ok' : 'refused') : ''}>
                {!input ? <div style={{ ...card, ...emptyLine }} data-pay-incomplete>{t('prr_pay_incomplete')}</div> : !p ? <div style={card}><Loader /></div> : (
                  <>
                    {p.refusals.length > 0 && <PayRefusals refusals={p.refusals} t={t} accounts={accounts} fix={refusalFix} />}
                    {e && (
                      <div style={card} data-pay-entry>
                        <div style={sectionHead}>{t('prr_pay_entry_head')}</div>
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
                          {kv(t('prr_f_entry_date'), <span data-entry-date={e.date}>{dmy(e.date)}</span>, 'date')}
                          {kv(t('prr_f_book'), <span data-entry-book={e.book}>{bookWord(t, e.book)}{e.bankBookAccount ? ` · ${e.bankBookAccount}` : ''}</span>, 'book')}
                          {kv(t('prr_col_doc_no'), <span data-entry-doc={e.docNo}>{e.docNo}</span>, 'doc_no')}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }} data-entry-narration>{e.narration}</div>
                        <EntryLines lines={e.lines} totalSat={p.totalSat} t={t} />
                      </div>
                    )}
                    {p.lines.length > 0 && (
                      <div style={card} data-pay-after>
                        <div style={sectionHead}>{t('prr_pay_after_head')}</div>
                        <table style={table}>
                          <tbody>
                            {p.lines.map(x => (
                              <tr key={x.lineId} data-pay-after-line={x.lineId} data-status-after={x.statusAfter}>
                                <td style={td}>{x.name}</td><td style={num}>{m2(x.amountSat)}</td>
                                <td style={{ ...num, color: 'var(--text-tertiary)' }}>{t('prr_pay_unpaid_after', { amount: m2(x.unpaidAfterSat) })}</td>
                                <td style={td}><PayStatusChip status={x.statusAfter} t={t} /></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </section>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}

// ── The run window's Payments tab (100 a page): each payment, its items folded, its entry, its reversal ─────────────
// Read only, so every seat that sees the run sees it; Reverse is drawn for a seat that may write and only where the
// payment's own readiness allows it — otherwise the refusal says why (a payment whose period has closed, an account
// switched off since).
export function PaymentsTab({ ctx, runId, readOnly, reloadKey, onReversed }: { ctx: AwCtx; runId: string; readOnly: boolean; reloadKey: number; onReversed: () => void }) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [data, setData] = useState<PrrPage<PrrPayment> | null>(null);
  const [err, setErr] = useState('');
  const [reload, setReload] = useState(0);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [rev, setRev] = useState<PrrPayment | null>(null);
  useEffect(() => {
    let alive = true;
    payrollRunsApi.payments(ctx.clientId, runId, page).then(d => { if (alive) { setData(d); setErr(''); } }).catch((e: any) => { if (alive) setErr(payErr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, runId, page, reloadKey, reload]);   // eslint-disable-line react-hooks/exhaustive-deps
  if (err) return <div style={errBox} data-payments-error>{err}</div>;
  if (!data) return <Loader />;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-payments={data.total}>
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {!data.rows.length ? <div style={emptyLine} data-payments-none>{t('prr_payments_none')}</div> : (
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>{t('prr_pcol_no')}</th><th style={th}>{t('prr_pcol_date')}</th><th style={th}>{t('prr_pcol_method')}</th><th style={th}>{t('prr_pcol_book')}</th>
                <th style={th}>{t('prr_pcol_account')}</th><th style={th}>{t('prr_pcol_line')}</th><th style={th}>{t('prr_pcol_slip')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('prr_pcol_total')}</th><th style={th}>{t('prr_pcol_by')}</th><th style={th}>{t('prr_pcol_state')}</th><th style={th} aria-hidden />
              </tr>
            </thead>
            <tbody>
              {data.rows.map(x => (
                <React.Fragment key={x.id}>
                  <tr data-payment={x.id} data-pay-no={x.payNo} data-state={x.state} style={x.state === 'reversed' ? { color: 'var(--text-tertiary)' } : undefined}>
                    <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                      {x.payNo}
                      <div><button style={{ ...linkBtn, fontWeight: 600 }} onClick={() => setOpen(o => ({ ...o, [x.id]: !o[x.id] }))} data-payment-items={x.id} aria-expanded={!!open[x.id]}>
                        <i className={`ti ti-chevron-${open[x.id] ? 'down' : 'right'}`} aria-hidden /> {t('prr_pay_items', { count: x.items.length })}
                      </button></div>
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{dmy(x.payDate)}</td>
                    <td style={td}>{t(`prr_pay_method_${x.method}`)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }} data-payment-page={x.pageNo ?? ''}>
                      {bookWord(t, x.sourceBook)}{x.bankBookAccount ? ` · ${x.bankBookAccount}` : ''}
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{x.entryDocNo}{x.pageNo !== null && x.pageNo !== undefined ? ` · ${t('prr_pay_page', { page: x.pageNo })}` : ''}</div>
                    </td>
                    <td style={td}>{x.accountCode}</td>
                    <td style={{ ...td, fontSize: 12 }} data-payment-line={x.bankLineId || ''}>{x.bankLine ? lineText(x.bankLine) : '—'}</td>
                    <td style={td}>{x.slipRef || '—'}</td>
                    <td style={{ ...num, fontWeight: 700 }} data-payment-total={x.totalSat}>{m2(x.totalSat)}</td>
                    <td style={{ ...td, fontSize: 12 }}>{x.createdByName}<div style={{ color: 'var(--text-tertiary)' }}>{x.createdAt ? fmtDateTime(x.createdAt) : ''}</div></td>
                    <td style={{ ...td, fontSize: 12 }}>
                      <span style={x.state === 'reversed' ? pillRed : pillGreen} data-payment-state={x.state}>{t(x.state === 'reversed' ? 'prr_pstate_reversed' : 'prr_pstate_live')}</span>
                      {x.reversal && <div style={{ marginTop: 3 }} data-payment-reversal={x.reversal.docNo}>{t('prr_pay_reversed_by', { docNo: x.reversal.docNo, page: x.reversal.pageNo ?? '—' })}</div>}
                      {x.reverseReason && <div style={{ color: 'var(--text-secondary)' }}>{t('prr_log_reason', { reason: x.reverseReason })}</div>}
                      {x.reversedByName && <div style={{ color: 'var(--text-tertiary)' }}>{x.reversedByName}{x.reversedAt ? ` · ${fmtDateTime(x.reversedAt)}` : ''}</div>}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {!readOnly && x.state === 'live' && (x.readiness?.reverse?.ok
                        ? <button style={{ ...actBtn, color: '#B45309' }} onClick={() => setRev(x)} data-payment-reverse={x.id}><i className="ti ti-arrow-back" aria-hidden /> {t('prr_pay_reverse')}</button>
                        : <span style={{ fontSize: 11.5, color: '#92400E', whiteSpace: 'normal' }} data-payment-reverse-refused={x.id}>{(x.readiness?.reverse?.refusals || []).map(r => payRefusalText(t, r)).join(' · ')}</span>)}
                    </td>
                  </tr>
                  {open[x.id] && x.items.map(i => (
                    <tr key={`${x.id}:${i.lineId}`} data-payment-item={i.lineId}>
                      <td style={td} /><td style={{ ...td, fontSize: 12 }} colSpan={6}>{i.name}</td>
                      <td style={{ ...num, fontSize: 12 }}>{m2(i.amountSat)}</td><td style={td} colSpan={3} />
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <Pagination page={page} total={data.total} limit={data.limit || 100} onPage={setPage} />
      <div style={note}>{t('prr_payments_note')}</div>
      {rev && <ReversePaymentDialog ctx={ctx} payment={rev} onClose={() => setRev(null)} onDone={() => { setRev(null); setReload(n => n + 1); onReversed(); }} />}
    </div>
  );
}

// ── Reverse a payment (a card; its form is the reason) ───────────────────────────────────────────────────────
// The server's dry run (GET /payments/:id/reverse-preview): the reversing entry — the payment's lines swapped, dated as
// the payment, in its period (R8-12) — and the statement line it frees. Reverse needs a reason and no refusal.
export function ReversePaymentDialog({ ctx, payment, onClose, onDone }: { ctx: AwCtx; payment: PrrPayment; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [pv, setPv] = useState<PrrPayReversePreview | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };
  useEffect(() => {
    let alive = true;
    payrollRunsApi.paymentReversePreview(ctx.clientId, payment.id).then(x => { if (alive) { setPv(x); setErr(''); } }).catch((e: any) => { if (alive) setErr(payErr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, payment.id]);   // eslint-disable-line react-hooks/exhaustive-deps
  const ready = !!pv && pv.ok && !!reason.trim();
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    try {
      await payrollRunsApi.reversePayment(ctx.clientId, payment.id, { reason: reason.trim() });
      setDirty(false); onDone();
    } catch (e: any) { setErr(payErr(e, t)); } finally { setBusy(false); }
  };
  return (
    <Dialog width={600} title={t('prr_pay_reverse_title')} docNo={`${payment.runDocNo} · ${payment.payNo}`} onClose={onClose} busy={busy} dirty={dirty}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon="ti ti-arrow-back" label={t('prr_pay_reverse_go')} t={t} />}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }} data-pay-reverse-form>
        <div style={note}>{t('prr_pay_reverse_note')}</div>
        {err && <div style={errBox} data-pay-reverse-error>{err}</div>}
        {!pv ? (err ? null : <Loader />) : (
          <>
            {pv.refusals.length > 0 && <PayRefusals refusals={pv.refusals} t={t} />}
            {pv.reversal && (
              <section style={card} data-pay-reverse-entry>
                <div style={sectionHead}>{t('prr_reverse_entry_head', { date: dmy(pv.reversal.date) })}</div>
                <EntryLines lines={pv.reversal.lines} totalSat={pv.reversal.totalSat} t={t} />
              </section>
            )}
            {pv.releasesLine && <div style={{ ...card, fontSize: 12.5 }} data-pay-reverse-frees={pv.releasesLine.id}>{t('prr_pay_reverse_frees', { line: lineText(pv.releasesLine) })}</div>}
          </>
        )}
        <label htmlFor="prr-pay-rev-reason" style={lbl}>{t('prr_reason')}</label>
        <textarea id="prr-pay-rev-reason" value={reason} onChange={ev => ed(setReason, ev.target.value)} rows={3} maxLength={PRR_MAX_TEXT} style={{ ...field, ...(!reason.trim() ? amberBox : {}) }} data-pay-reverse-reason />
      </div>
    </Dialog>
  );
}
